/** Operating controls, latches, mandate and limits: why every action is or is not available (U046-U050). */
import {
  ROUTING_FACTS,
  decodeBalanceRow,
  decodeControlRow,
  decodeLatchRow,
  decodeLimitRow,
  decodeRoutingFacts,
  type ControlRow,
  type LatchRow,
  type LimitRow,
  type RoutingFacts,
} from "../../contracts/account.ts";
import {
  badge,
  definitions,
  instantCell,
  metric,
  renderFreshnessBar,
} from "../../app/components.ts";
import { h, setChildren, type Child } from "../../app/dom.ts";
import type { Mode } from "../../contracts/envelope.ts";
import { formatPaise } from "../../app/format.ts";
import { acknowledge } from "../../app/latches.ts";
import { codeGuardOf, type RoutingGuard } from "../../app/command-dialog.ts";
import { factsRowOf, resumeGate } from "../../app/resume-gate.ts";
import { renderStateBanner, stateOfEnvelope, stateOfError } from "../../app/state.ts";
import { anchor, type ServerClock } from "../../app/time.ts";
import type { ViewContext, ViewHandle } from "../../app/view.ts";
import { CommandSurface } from "../command-surface.ts";
import { handleOf, makeFrame } from "../frame.ts";
import { ackKey, blockedBecause, controlOf, entriesState, latchView } from "./model.ts";

const G5 = "Not enabled before G5: broker routing is off, so this action does not exist yet.";

function scopeOf(accountId: string, control: ControlRow | undefined): string {
  return control === undefined ? `account ${accountId}` : `account ${accountId} (${control.scope})`;
}

function entriesSection(
  controls: readonly ControlRow[],
  surface: CommandSurface,
  accountId: string,
  accountVersion: number | undefined,
  facts: RoutingFacts | null,
  clock: ServerClock | null,
  mode: Mode | null,
): HTMLElement {
  const entries = controlOf(controls, "ENTRIES");
  const state = entriesState(controls);
  const pause = surface.launcher({
    intent: "pause-entries",
    scope: scopeOf(accountId, entries),
    quantities: "new entries only; open positions and working orders are not touched",
    payload: { scope: "ACCOUNT" },
    applicable: state === "ENABLED",
    inapplicable: state === "ENABLED" ? undefined : `New entries are ${state.toLowerCase()}.`,
  });
  // Resume goes through the INC-0807 gate: the cooldown, the written review and the out-of-band
  // code are the routing page's, built from the same facts (Item 151).
  const gate = resumeGate(facts, state === "PAUSED", clock, mode);
  const resume = surface.offered("resume")
    ? surface.launcher({
        intent: "resume",
        scope: scopeOf(accountId, entries),
        quantities: "new entries are paused; open positions are unchanged",
        payload: { scope: "ACCOUNT" },
        ...(gate.guard === undefined ? {} : { routing: gate.guard }),
        applicable: gate.ready,
        inapplicable: gate.blocker,
      })
    : null;
  return h(
    "section",
    { attrs: { "aria-labelledby": "risk-entries" } },
    h("h2", { attrs: { id: "risk-entries" } }, "New entries"),
    definitions([
      ["State", badge(state, state === "ENABLED" ? "ok" : "warn")],
      ["Since", entries === undefined ? metric(null) : instantCell(entries.since)],
      ["Policy version", entries?.policyVersion ?? "unknown"],
      [
        "Account state version",
        metric(accountVersion === undefined ? null : `v${String(accountVersion)}`),
      ],
    ]),
    h(
      "p",
      { class: "note" },
      "Pausing stops new entries only. It does not close positions or cancel orders, and it is a different command from reducing, flattening or cancelling.",
    ),
    h("div", { class: "actions" }, pause, resume),
    resume === null ? h("p", { class: "g5" }, `Resume: ${G5}`) : null,
  );
}

function protectionSection(surface: CommandSurface, accountId: string): HTMLElement {
  const scope = `account ${accountId}`;
  const flatten = surface.offered("flatten-account")
    ? surface.launcher({
        intent: "flatten-account",
        scope,
        quantities: "every open position in the account",
        payload: { scope: "ACCOUNT" },
      })
    : null;
  const cancelAll = surface.offered("cancel-all-orders")
    ? surface.launcher({
        intent: "cancel-all-orders",
        scope,
        quantities: "every working order in the account, protective stops included",
        payload: { scope: "ACCOUNT" },
      })
    : null;
  return h(
    "section",
    { attrs: { "aria-labelledby": "risk-protection" } },
    h("h2", { attrs: { id: "risk-protection" } }, "Positions and orders"),
    flatten === null
      ? h("p", { class: "g5" }, `Flatten account and Cancel all orders: ${G5}`)
      : h(
          "div",
          {},
          h(
            "p",
            { class: "warning" },
            "Cancel all orders also cancels protective stop orders and can leave positions without protection. It needs its own authority, apart from cancelling one order. Reduce one position from the account page.",
          ),
          h("div", { class: "actions" }, flatten, cancelAll),
        ),
  );
}

/** A takeover is risk-increasing: it needs a code in ASSISTED and LIVE (no clock, no guard: nothing to check). */
function takeoverGuard(
  facts: RoutingFacts | null,
  clock: ServerClock,
  mode: Mode | null,
): { readonly routing?: RoutingGuard } {
  const code = codeGuardOf("manual-takeover", facts ?? { outOfBand: null }, mode);
  return Object.keys(code).length === 0 ? {} : { routing: { clock, ...code } };
}

function ownershipSection(
  controls: readonly ControlRow[],
  surface: CommandSurface,
  accountId: string,
  facts: RoutingFacts | null,
  clock: ServerClock,
  mode: Mode | null,
): HTMLElement {
  const owner = controlOf(controls, "EXECUTION_OWNER");
  const mandate = controlOf(controls, "MANDATE");
  const readiness = controlOf(controls, "READINESS");
  const takeover = surface.offered("manual-takeover")
    ? surface.launcher({
        intent: "manual-takeover",
        scope: scopeOf(accountId, owner),
        quantities: "execution ownership only; positions and orders are unchanged",
        payload: { scope: "ACCOUNT" },
        ...takeoverGuard(facts, clock, mode),
        applicable: owner?.state === "ENABLED",
        inapplicable:
          owner?.state === "ENABLED" ? undefined : "The controller does not own execution now.",
      })
    : null;
  if (takeover === null) {
    return h(
      "section",
      { attrs: { "aria-labelledby": "risk-owner" } },
      h("h2", { attrs: { id: "risk-owner" } }, "Execution owner and mandate"),
      h("p", { class: "g5" }, G5),
    );
  }
  return h(
    "section",
    { attrs: { "aria-labelledby": "risk-owner" } },
    h("h2", { attrs: { id: "risk-owner" } }, "Execution owner and mandate"),
    definitions([
      [
        "Execution owner",
        owner === undefined
          ? metric(null)
          : badge(owner.state === "MANUAL" ? "OPERATOR" : "CONTROLLER", "info"),
      ],
      ["Mandate scope", mandate?.scope ?? "none"],
      [
        "Mandate expires",
        mandate?.expiresAt == null ? metric(null) : instantCell(mandate.expiresAt),
      ],
      [
        "Mandate revoked",
        mandate === undefined ? "unknown" : mandate.revoked ? "revoked" : "not revoked",
      ],
      [
        "Readiness",
        readiness === undefined
          ? metric(null)
          : badge(readiness.state, readiness.state === "BLOCKED" ? "bad" : "ok"),
      ],
    ]),
    readiness === undefined || readiness.blockers.length === 0
      ? null
      : h(
          "ul",
          { class: "blockers" },
          ...readiness.blockers.map((blocker) => h("li", {}, blocker)),
        ),
    h(
      "p",
      { class: "note" },
      "Logging out, closing the tab and a browser refresh do not hand ownership back and never re-arm anything.",
    ),
    h("div", { class: "actions" }, takeover),
  );
}

function latchesSection(
  latches: readonly LatchRow[],
  ctx: ViewContext,
  redraw: () => void,
): HTMLElement {
  const local = ctx.acknowledgements.get();
  return h(
    "section",
    { attrs: { "aria-labelledby": "risk-latches" } },
    h("h2", { attrs: { id: "risk-latches" } }, "Safety latches"),
    h(
      "p",
      { class: "note" },
      "Acknowledging an alert, logging out, closing the tab and quiet hours release nothing and send no command. A latch is released only by the command it names.",
    ),
    latches.length === 0
      ? h("p", {}, "No latches reported.")
      : h(
          "ul",
          { class: "latches" },
          ...latches.map((row) => {
            const view = latchView(row, local);
            const ack: Child =
              view.held && !view.acknowledged
                ? h(
                    "button",
                    {
                      attrs: {
                        type: "button",
                        "data-acknowledge": row.latchId,
                        "aria-label": `Acknowledge the alert for ${row.label} on ${row.scope} (display only; releases nothing)`,
                      },
                      on: {
                        click: () => {
                          acknowledge(ctx.acknowledgements, ackKey(row.latchId));
                          redraw();
                        },
                      },
                    },
                    "Acknowledge alert",
                  )
                : null;
            return h(
              "li",
              { attrs: { "data-latch": row.latchId, "data-latch-state": row.state } },
              h("strong", {}, `${row.label} `),
              badge(row.state, view.held ? "bad" : "ok"),
              ` ${row.scope}: ${row.reason}. Engaged `,
              instantCell(row.engagedAt),
              row.releasedAt === null ? "" : ", released ",
              row.releasedAt === null ? null : instantCell(row.releasedAt),
              ". ",
              h("span", { class: "latch-text" }, view.text),
              " ",
              ack,
            );
          }),
        ),
  );
}

function limitsSection(limits: readonly LimitRow[]): HTMLElement {
  return h(
    "section",
    { attrs: { "aria-labelledby": "risk-limits" } },
    h("h2", { attrs: { id: "risk-limits" } }, "Limits and deadlines"),
    h(
      "ul",
      { class: "limits" },
      ...limits.map((limit) =>
        h(
          "li",
          { attrs: { "data-limit": limit.limitId, "data-blocking": String(limit.blocking) } },
          h("strong", {}, `${limit.label} `),
          limit.blocking ? badge("BLOCKING", "bad") : badge("not blocking", "ok"),
          limit.limitPaise === null || limit.usedPaise === null
            ? null
            : ` used ${formatPaise(limit.usedPaise)} of ${formatPaise(limit.limitPaise)}`,
          limit.deadline === null ? null : h("span", {}, " due ", instantCell(limit.deadline)),
          ` (policy ${limit.policyVersion}). ${limit.explanation}`,
        ),
      ),
    ),
  );
}

interface Loaded {
  readonly accountVersion: number | undefined;
  readonly controls: readonly ControlRow[];
  readonly latches: readonly LatchRow[];
  readonly limits: readonly LimitRow[];
  /** The routing facts for the resume gate; `null` when not needed or unreadable (fails closed). */
  readonly facts: RoutingFacts | null;
  readonly factsClock: ServerClock | null;
  /** The balances' snapshot clock: the guard of a command that needs no facts. */
  readonly clock: ServerClock;
  /** The mode that decides whether a risk-increasing command needs a code (the facts', else the balances'). */
  readonly mode: Mode | null;
}

/** Mount the risk view: controls, latches, mandate, limits and what each blocks. */
export function mountRisk(ctx: ViewContext): ViewHandle {
  const frame = makeFrame("risk", "Risk and operating controls");
  const accountId = ctx.accountId;
  if (accountId === null) {
    frame.banner.append(renderStateBanner({ kind: "no-observations" }));
    return handleOf(frame, ctx);
  }
  const account: string = accountId;
  frame.banner.append(renderStateBanner({ kind: "loading" }));
  const surface = new CommandSurface(ctx, load);

  const draw = (data: Loaded): void => {
    const blocked = blockedBecause(data.controls, data.latches, data.limits);
    surface.clearLaunchers();
    setChildren(frame.body, [
      surface.status,
      entriesSection(
        data.controls,
        surface,
        accountId,
        data.accountVersion,
        data.facts,
        data.factsClock,
        data.mode,
      ),
      protectionSection(surface, accountId),
      ownershipSection(data.controls, surface, accountId, data.facts, data.clock, data.mode),
      latchesSection(data.latches, ctx, () => {
        draw(data);
      }),
      limitsSection(data.limits),
      h(
        "section",
        { attrs: { "aria-labelledby": "risk-blocked" } },
        h("h2", { attrs: { id: "risk-blocked" } }, "Why an action may be blocked"),
        blocked.length === 0
          ? h("p", {}, "Nothing is blocking an action.")
          : h("ul", { class: "blocked" }, ...blocked.map((reason) => h("li", {}, reason))),
      ),
    ]);
    surface.sync();
  };

  // A function call, so the check after an await is not narrowed away by the earlier one.
  const disposed = (): boolean => ctx.scope.disposed;

  /** The routing facts the resume gate needs; any failure leaves Resume blocked, never guessed. */
  async function readFacts(controls: readonly ControlRow[]) {
    if (!surface.offered("resume") || entriesState(controls) !== "PAUSED") {
      return null;
    }
    try {
      const envelope = await ctx.client.read(ROUTING_FACTS, decodeRoutingFacts, {
        accountId: account,
      });
      return envelope === null ? null : { envelope, facts: factsRowOf(envelope, account) };
    } catch {
      return null;
    }
  }

  async function load(): Promise<void> {
    surface.reset();
    const params = { accountId };
    try {
      const [controls, latches, limits, balances] = await Promise.all([
        ctx.client.read("risk_controls", decodeControlRow, params),
        ctx.client.read("latches", decodeLatchRow, params),
        ctx.client.read("limits", decodeLimitRow, params),
        ctx.client.read("balances", decodeBalanceRow, params),
      ]);
      if (
        controls === null ||
        latches === null ||
        limits === null ||
        balances === null ||
        ctx.scope.disposed
      ) {
        return;
      }
      const state = stateOfEnvelope(controls);
      setChildren(frame.banner, state.kind === "ready" ? [] : [renderStateBanner(state)]);
      setChildren(frame.freshness, [
        renderFreshnessBar(controls, { streamSequence: ctx.streamSequence.get() }),
      ]);
      const found = await readFacts(controls.page.rows);
      if (disposed()) {
        return;
      }
      draw({
        accountVersion: balances.page.rows[0]?.accountVersion,
        controls: controls.page.rows,
        latches: latches.page.rows,
        limits: limits.page.rows,
        facts: found?.facts ?? null,
        // The facts' own mode when they were read (null fails closed), else the balances' (review F2).
        mode: found === null ? balances.mode : found.envelope.mode,
        clock: anchor(balances.snapshotTime, ctx.mono),
        factsClock: found === null ? null : anchor(found.envelope.snapshotTime, ctx.mono),
      });
      surface.observe("risk_controls", controls);
      surface.observe("latches", latches);
      surface.observe("limits", limits);
      surface.observe("balances", balances);
      if (found !== null) {
        // Stale facts disable the resume like any other gate (review F1 of INC-0807).
        surface.observe(ROUTING_FACTS, found.envelope);
      }
      surface.identifyFrom(balances);
    } catch (error) {
      setChildren(frame.banner, [renderStateBanner(stateOfError(error))]);
    }
  }

  void load();
  return handleOf(frame, ctx);
}
